import {
  AloftArea,
  AloftPrice,
  CraftsmanCostProps,
  getCraftsmanCost,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectEffectiveDate } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateMultiFamilyCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const propertyIdentificationForTransaction = propertyIdentification[id];

  if (
    isUndefined(improvementsForTransaction) ||
    isUndefined(propertyIdentificationForTransaction) ||
    isUndefined(effectiveDate)
  ) {
    return undefined;
  }

  const { gla, craftsmanQuality: quality, propertyType } = improvementsForTransaction;
  const {
    address: { PostalCode: zipCode },
  } = propertyIdentificationForTransaction;

  const numberOfUnits =
    propertyType === 'multiFamily' ? improvementsForTransaction.numberOfUnits : undefined;

  if (
    isUndefined(zipCode) ||
    isUndefined(gla) ||
    isUndefined(numberOfUnits) ||
    isUndefined(quality) ||
    (quality * 10) % 1 !== 0 ||
    quality < 1 ||
    numberOfUnits < 1
  ) {
    return undefined;
  }

  const avgUnitSqft = AloftArea.schema.parse({
    unitAmount: gla.unitAmount / numberOfUnits,
    unit: gla.unit,
    $type: gla.$type,
  });

  const calculator = getCraftsmanCost(costProps);
  return calculator.getMultiFamilyCost({
    quality,
    avgUnitSqft,
    units: numberOfUnits,
    zipCode,
  });
};

const getTotalMultiFamilyCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
) => {
  const costOutput = calculateMultiFamilyCost(
    costProps,
    improvements,
    propertyIdentification,
    effectiveDate,
    id,
  );

  if (costOutput === undefined) {
    return AloftPrice.zero;
  }

  return costOutput.totalCost;
};

export const selectMultiFamilyCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectEffectiveDate,
    (_state, id: string) => id,
  ],
  getTotalMultiFamilyCost,
);

export const selectMultiFamilyCostOutput = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectEffectiveDate,
    (_state, id: string) => id,
  ],
  calculateMultiFamilyCost,
);

const selectMultiFamilyUnitsCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectEffectiveDate,
    (_state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, effectiveDate, id) => {
    const costOutput = calculateMultiFamilyCost(
      costProps,
      improvements,
      propertyIdentification,
      effectiveDate,
      id,
    );

    if (costOutput === undefined || costOutput.fixed === undefined) {
      return AloftPrice.zero;
    }

    return costOutput.fixed;
  },
);

const selectMultiFamilyGBACost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectEffectiveDate,
    (_state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, effectiveDate, id) => {
    const costOutput = calculateMultiFamilyCost(
      costProps,
      improvements,
      propertyIdentification,
      effectiveDate,
      id,
    );
    const area = improvements[id]?.gla;

    if (costOutput === undefined || costOutput.variable === undefined || area === undefined) {
      return AloftPrice.zero;
    }

    return costOutput.variable.mul(area.unitAmount);
  },
);

export const selectMultiFamilyUnitsCostDisplay = createSelector(
  [selectMultiFamilyUnitsCost],
  (totalUnitCost) => {
    if (isUndefined(totalUnitCost)) {
      return undefined;
    }

    return totalUnitCost.toString(true);
  },
);

export const selectMultiFamilyGBACostDisplay = createSelector([selectMultiFamilyGBACost], (cost) =>
  cost?.toString(true),
);
